import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";

interface OverlayEntry {
  id: string;
  triggerRef: React.RefObject<HTMLElement | null>;
  close: () => void;
  level: 1 | 2; // Level 1: modal/sheet/palette. Level 2: popover/tooltip
}

interface OverlayStackContextValue {
  stack: OverlayEntry[];
  push: (entry: OverlayEntry) => void;
  pop: (id: string) => void;
  closeTopmost: () => void;
  isTopmost: (id: string) => boolean;
}

const OverlayStackContext = createContext<OverlayStackContextValue | null>(
  null,
);

export function OverlayStackProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<OverlayEntry[]>([]);
  const stackRef = useRef(stack);
  stackRef.current = stack;

  const push = useCallback((entry: OverlayEntry) => {
    setStack((prev) => {
      // Command palette replaces any open Level 1 overlay (UX spec rule)
      if (entry.id === "command-palette") {
        const level1 = prev.find((e) => e.level === 1);
        if (level1) {
          level1.close();
          return [...prev.filter((e) => e.level !== 1), entry];
        }
      }
      // Never two Level 1 overlays simultaneously
      if (entry.level === 1 && prev.some((e) => e.level === 1)) {
        const existing = prev.find((e) => e.level === 1);
        existing?.close();
        return [...prev.filter((e) => e.level !== 1), entry];
      }
      return [...prev, entry];
    });
  }, []);

  const pop = useCallback((id: string) => {
    setStack((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (entry?.triggerRef.current) {
        // Restore focus to trigger element
        requestAnimationFrame(() => {
          entry.triggerRef.current?.focus();
        });
      }
      return prev.filter((e) => e.id !== id);
    });
  }, []);

  const closeTopmost = useCallback(() => {
    const current = stackRef.current;
    if (current.length === 0) return;
    const topmost = current[current.length - 1];
    topmost.close();
    // pop will be called by the overlay's own close handler
  }, []);

  const isTopmost = useCallback(
    (id: string) => {
      return stack.length > 0 && stack[stack.length - 1].id === id;
    },
    [stack],
  );

  return (
    <OverlayStackContext.Provider
      value={{ stack, push, pop, closeTopmost, isTopmost }}
    >
      {children}
    </OverlayStackContext.Provider>
  );
}

export function useOverlayStackContext() {
  const ctx = useContext(OverlayStackContext);
  if (!ctx)
    throw new Error(
      "useOverlayStackContext must be used within OverlayStackProvider",
    );
  return ctx;
}

export type { OverlayEntry, OverlayStackContextValue };
